// src/layouts/Sidebar.tsx

import React from 'react';

import { FaTachometerAlt } from 'react-icons/fa';

import {
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiActivity,
  FiPauseCircle,
  FiTrendingUp,
  FiAlertTriangle,
} from 'react-icons/fi';

import {
  useLocation,
  useNavigate,
} from 'react-router-dom';

import sagilityLogo from '../../../assets/Logo/sagility-logo-green-white.png';
import sagilityicon from '../../../assets/Logo/sagility-logo-icon.png';

type SidebarProps = {
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
};

type NavigationItem = {
  label: string;
  path: string;
  icon: React.ElementType;
};

type UserRole = 'Employee' | 'Manager';

/* ============================================================================
 * EMPLOYEE NAVIGATION
 * ========================================================================== */

const employeeNavigationItems: NavigationItem[] = [
  {
    label: 'Logged Hours',
    path: '/employee-logged-hours-report',
    icon: FiClock,
  },
  {
    label: 'Active Hours',
    path: '/employee-active-hours',
    icon: FiActivity,
  },
  {
    label: 'Idle Time',
    path: '/employee-idle-time',
    icon: FiPauseCircle,
  },
  {
    label: 'Productivity',
    path: '/emp-productivity-report',
    icon: FiTrendingUp,
  },
];

/* ============================================================================
 * MANAGER NAVIGATION
 * ========================================================================== */

const managerNavigationItems: NavigationItem[] = [
  {
    label: 'Team Active Hours',
    path: '/team-active-hours',
    icon: FiActivity,
  },
  {
    label: 'Productivity',
    path: '/rm-productivity-report',
    icon: FiTrendingUp,
  },
  {
    label: 'Idle Time',
    path: '/idle-time-report',
    icon: FiPauseCircle,
  },

  // {
  //   label: 'At Risk Associates',
  //   path: '/at-risk-associates',
  //   icon: FiAlertTriangle,
  // },
];

/* ============================================================================
 * GET CURRENT ROLE
 * ========================================================================== */

const getCurrentRole = (): UserRole => {
  /*
   * Prefer sessionStorage because role switching is normally stored there.
   * Fall back to localStorage for existing login/session data.
   */

  const sessionRole = sessionStorage.getItem('role');
  const localRole = localStorage.getItem('role');

  const role = sessionRole || localRole || '';

  const normalizedRole = role.trim().toLowerCase();

  if (
    normalizedRole === 'manager' ||
    normalizedRole === 'managerdashboard'
  ) {
    return 'Manager';
  }

  return 'Employee';
};

/* ============================================================================
 * SIDEBAR
 * ========================================================================== */

const Sidebar = ({
  expanded,
  setExpanded,
}: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  /* --------------------------------------------------------------------------
   * CURRENT ROLE
   * ------------------------------------------------------------------------ */

  const role = getCurrentRole();

  const isManager = role === 'Manager';

  /* --------------------------------------------------------------------------
   * ROLE BASED NAVIGATION
   * ------------------------------------------------------------------------ */

  const navigationItems = isManager
    ? managerNavigationItems
    : employeeNavigationItems;

  /* --------------------------------------------------------------------------
   * ROLE BASED DASHBOARD
   *
   * Manager:
   * /rm-dashboard
   *
   * Employee:
   * /employee-dashboard
   * ------------------------------------------------------------------------ */

  const dashboardPath = isManager
    ? '/rm-dashboard'
    : '/employee-dashboard';

  /* --------------------------------------------------------------------------
   * NAVIGATION
   * ------------------------------------------------------------------------ */

  const handleNavigation = (path: string) => {
    if (!path) {
      return;
    }

    navigate(path);
  };

  /* --------------------------------------------------------------------------
   * ACTIVE ROUTE
   * ------------------------------------------------------------------------ */

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  /* ==========================================================================
   * JSX
   * ======================================================================== */

  return (
    <>
      {/* ======================================================================
       * RESPONSIVE SIDEBAR STYLES
       *
       * Normal 100% browser zoom:
       *   Sidebar = 220px
       *
       * Higher browser zoom / smaller CSS viewport:
       *   Sidebar progressively becomes smaller.
       *
       * This keeps the existing design at 100% while preventing the sidebar
       * from taking too much horizontal space at 110% - 150% zoom.
       * ==================================================================== */}

      <style>
        {`
          /* ================================================================
           * DEFAULT - 100% ZOOM / LARGE VIEWPORT
           * ================================================================ */

          .workvista-sidebar {
            width: 220px;
          }

          .workvista-sidebar-button {
            height: 48px;
            padding-left: 16px;
            padding-right: 16px;
            border-radius: 12px;
          }

          .workvista-sidebar-label {
            font-size: 14px;
          }

          .workvista-sidebar-icon {
            font-size: 18px;
          }

          .workvista-sidebar-dashboard-icon {
            font-size: 15px;
          }

          .workvista-sidebar-toggle {
            width: 34px;
            height: 34px;
            right: -16px;
          }

          .workvista-sidebar-toggle-icon {
            font-size: 18px;
          }

          .workvista-sidebar-logo {
            height: 42px;
          }

          .workvista-sidebar-small-logo {
            width: 34px;
            height: 34px;
          }

          /* ================================================================
           * 110% - 125% ZOOM
           *
           * Effective CSS viewport becomes smaller.
           * Reduce sidebar width and internal spacing.
           * ================================================================ */

          @media (max-width: 1400px) {
            .workvista-sidebar {
              width: 205px;
            }

            .workvista-sidebar-button {
              height: 46px;
              padding-left: 14px;
              padding-right: 14px;
              border-radius: 11px;
            }

            .workvista-sidebar-label {
              font-size: 13px;
            }

            .workvista-sidebar-icon {
              font-size: 17px;
            }

            .workvista-sidebar-dashboard-icon {
              font-size: 14px;
            }

            .workvista-sidebar-logo {
              height: 39px;
            }

            .workvista-sidebar-small-logo {
              width: 32px;
              height: 32px;
            }

            .workvista-sidebar-toggle {
              width: 32px;
              height: 32px;
              right: -15px;
            }

            .workvista-sidebar-toggle-icon {
              font-size: 17px;
            }
          }

          /* ================================================================
           * 125% - 150% ZOOM
           *
           * More compact layout.
           * ================================================================ */

          @media (max-width: 1200px) {
            .workvista-sidebar {
              width: 190px;
            }

            .workvista-sidebar-button {
              height: 44px;
              padding-left: 12px;
              padding-right: 12px;
              border-radius: 10px;
            }

            .workvista-sidebar-label {
              font-size: 12px;
            }

            .workvista-sidebar-icon {
              font-size: 16px;
            }

            .workvista-sidebar-dashboard-icon {
              font-size: 13px;
            }

            .workvista-sidebar-logo {
              height: 36px;
            }

            .workvista-sidebar-small-logo {
              width: 30px;
              height: 30px;
            }

            .workvista-sidebar-toggle {
              width: 30px;
              height: 30px;
              right: -14px;
            }

            .workvista-sidebar-toggle-icon {
              font-size: 16px;
            }
          }

          /* ================================================================
           * VERY SMALL EFFECTIVE VIEWPORT
           *
           * Used when browser zoom is very high or the actual screen is small.
           * ================================================================ */

          @media (max-width: 1000px) {
            .workvista-sidebar {
              width: 180px;
            }

            .workvista-sidebar-button {
              height: 42px;
              padding-left: 10px;
              padding-right: 10px;
              border-radius: 9px;
            }

            .workvista-sidebar-label {
              font-size: 11px;
            }

            .workvista-sidebar-icon {
              font-size: 15px;
            }

            .workvista-sidebar-dashboard-icon {
              font-size: 12px;
            }

            .workvista-sidebar-logo {
              height: 33px;
            }

            .workvista-sidebar-small-logo {
              width: 28px;
              height: 28px;
            }

            .workvista-sidebar-toggle {
              width: 28px;
              height: 28px;
              right: -13px;
            }

            .workvista-sidebar-toggle-icon {
              font-size: 15px;
            }
          }

          /* ================================================================
           * COLLAPSED SIDEBAR
           *
           * Width is controlled by the React state.
           * The media queries above only apply when expanded.
           * ================================================================ */

          @media (max-width: 1400px) {
            .workvista-sidebar.collapsed {
              width: 70px !important;
            }
          }

          @media (max-width: 1200px) {
            .workvista-sidebar.collapsed {
              width: 64px !important;
            }
          }

          @media (max-width: 1000px) {
            .workvista-sidebar.collapsed {
              width: 60px !important;
            }
          }

          /* ================================================================
           * PREVENT TEXT FROM BREAKING BADLY
           * ================================================================ */

          .workvista-sidebar-label {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            line-height: 1.2;
          }

          /* ================================================================
           * SMOOTH TRANSITION
           * ================================================================ */

          .workvista-sidebar,
          .workvista-sidebar-button,
          .workvista-sidebar-logo,
          .workvista-sidebar-toggle {
            transition:
              width 0.3s ease,
              height 0.3s ease,
              padding 0.3s ease,
              font-size 0.3s ease;
          }
        `}
      </style>

      <aside
        className={`
          workvista-sidebar
          ${!expanded ? 'collapsed' : ''}
          fixed
          left-0
          top-0
          z-50
          h-screen
          bg-[#002B5B]
          transition-all
          duration-300
        `}
        style={{
          width: expanded ? undefined : '78px',
        }}
      >
        {/* ====================================================================
         * COLLAPSE / EXPAND BUTTON
         * ================================================================== */}

        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="
            workvista-sidebar-toggle
            absolute
            top-[18px]
            z-[60]
            flex
            items-center
            justify-center
            rounded-full
            border
            border-[#D9E2EC]
            bg-white
            text-[#64748B]
            shadow-md
            transition-all
            hover:bg-[#F8FAFC]
          "
          aria-label={
            expanded
              ? 'Collapse sidebar'
              : 'Expand sidebar'
          }
        >
          {expanded ? (
            <FiChevronLeft
              className="workvista-sidebar-toggle-icon"
            />
          ) : (
            <FiChevronRight
              className="workvista-sidebar-toggle-icon"
            />
          )}
        </button>

        {/* ====================================================================
         * LOGO
         * ================================================================== */}

        <div className="flex flex-col items-center pt-5">
          {expanded ? (
            <img
              src={sagilityLogo}
              alt="Sagility"
              className="
                workvista-sidebar-logo
                w-auto
                object-contain
              "
            />
          ) : (
            <img
              src={sagilityicon}
              alt="Sagility"
              className="
                workvista-sidebar-small-logo
                object-contain
              "
            />
          )}

          {/* Divider */}

          <div
            className="
              mt-5
              h-[1px]
              w-[84%]
              bg-[#1A4D82]
            "
          />

          {/* ==================================================================
           * NAVIGATION
           * ================================================================= */}

          <nav className="mt-5 w-full px-3">

            {/* =================================================================
             * DASHBOARD
             * =============================================================== */}

            <button
              type="button"
              onClick={() => handleNavigation(dashboardPath)}
              title={
                expanded
                  ? undefined
                  : 'Dashboard'
              }
              className={`
                workvista-sidebar-button
                mb-2
                flex
                w-full
                items-center
                transition-all
                duration-200

                ${
                  isActive(dashboardPath)
                    ? `
                      bg-[#0A4A93]
                      text-white
                      shadow-sm
                    `
                    : `
                      bg-transparent
                      text-[#D7E5F5]
                      hover:bg-[#0B57AB]
                    `
                }

                ${
                  expanded
                    ? 'justify-start'
                    : 'justify-center'
                }
              `}
            >
              <FaTachometerAlt
                className="
                  workvista-sidebar-dashboard-icon
                  shrink-0
                "
              />

              {expanded && (
                <span
                  className="
                    workvista-sidebar-label
                    ml-3
                    font-semibold
                  "
                >
                  Dashboard
                </span>
              )}
            </button>

            {/* =================================================================
             * ROLE BASED NAVIGATION
             * =============================================================== */}

            {navigationItems.map(
              ({
                label,
                path,
                icon: Icon,
              }) => {
                const active = isActive(path);

                return (
                  <button
                    key={path}
                    type="button"
                    onClick={() =>
                      handleNavigation(path)
                    }
                    title={
                      expanded
                        ? undefined
                        : label
                    }
                    className={`
                      workvista-sidebar-button
                      mb-2
                      flex
                      w-full
                      items-center
                      transition-all
                      duration-200

                      ${
                        active
                          ? `
                            bg-[#0A4A93]
                            text-white
                            shadow-sm
                          `
                          : `
                            bg-transparent
                            text-[#D7E5F5]
                            hover:bg-[#0B57AB]
                          `
                      }

                      ${
                        expanded
                          ? 'justify-start'
                          : 'justify-center'
                      }
                    `}
                  >
                    <Icon
                      className="
                        workvista-sidebar-icon
                        shrink-0
                      "
                    />

                    {expanded && (
                      <span
                        className={`
                          workvista-sidebar-label
                          ml-3

                          ${
                            active
                              ? 'font-semibold'
                              : 'font-medium'
                          }
                        `}
                      >
                        {label}
                      </span>
                    )}
                  </button>
                );
              },
            )}
          </nav>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;